import { supabase } from '../lib/supabase'
import {
  FinanceiroPastaJaProcessadaError,
  type FinanceiroComissaoRow,
  type FinanceiroComissoesIcatuPasta,
  type SaveComissoesIcatuInput,
} from '../types/financeiro'

const BATCH_SIZE = 500

const FOLDERS_TABLE = 'finan_icatu_commission_folders'
const ROWS_TABLE = 'finan_icatu_commissions'

function rowToInsert(
  row: FinanceiroComissaoRow,
  folderId: string,
  uploadedBy: string | null
) {
  return {
    folder_id: folderId,
    folder_name: row.pasta,
    file_name: row.arquivo,
    codigo_corretor: row.codigoCorretor,
    nome_corretor: row.nomeCorretor,
    cpf_cnpj: row.cpfCnpj,
    apolice: row.apolice,
    numero_sub: row.numeroSub,
    modulo: row.modulo,
    estipulante: row.estipulante,
    sub_estipulante: row.subEstipulante,
    fatura: row.fatura,
    competencia: row.competencia,
    valor_apropriado: row.valorApropriado,
    tipo_comissao: row.tipoComissao,
    parcela_inicial: row.parcelaInicial,
    numero_grupo_cobertura: row.numeroGrupoCobertura,
    valor_base: row.valorBase,
    percentual_comissao: row.percentualComissao,
    valor_comissao: row.valorComissao,
    data_emissao: row.dataEmissao,
    data_apropriacao: row.dataApropriacao,
    valor_premio_bruto: row.valorPremioBruto,
    valor_premio_tarifario: row.valorPremioTarifario,
    data_movimento: row.dataMovimento,
    data_agendamento: row.dataAgendamento,
    data_pagamento: row.dataPagamento,
    data_pagamento_efetivo: row.dataPagamentoEfetivo,
    status_recibo: row.statusRecibo,
    nr_cobranca: row.nrCobranca,
    uploaded_by: uploadedBy,
  }
}

export const financeiroService = {
  async listPastasProcessadas(): Promise<FinanceiroComissoesIcatuPasta[]> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('*')
      .order('folder_name', { ascending: true })

    if (error) throw error
    return (data ?? []) as FinanceiroComissoesIcatuPasta[]
  },

  async isPastaProcessada(pastaNome: string): Promise<boolean> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('id')
      .eq('folder_name', pastaNome)
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Grava uma pasta processada + linhas do extrato.
   * Se a pasta já existir, lança FinanceiroPastaJaProcessadaError sem inserir linhas.
   */
  async saveComissoesIcatu(input: SaveComissoesIcatuInput): Promise<FinanceiroComissoesIcatuPasta> {
    const pastaNome = input.pastaNome.trim()
    if (!pastaNome) throw new Error('Nome da pasta é obrigatório.')

    const already = await this.isPastaProcessada(pastaNome)
    if (already) throw new FinanceiroPastaJaProcessadaError(pastaNome)

    const { data: pasta, error: pastaError } = await supabase
      .from(FOLDERS_TABLE)
      .insert({
        folder_name: pastaNome,
        year: input.ano,
        root_folder_name: input.rootFolderName,
        files_ok: input.arquivosOk,
        files_error: input.arquivosErro,
        row_count: input.rows.length,
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .select('*')
      .single()

    if (pastaError) {
      if (pastaError.code === '23505') {
        throw new FinanceiroPastaJaProcessadaError(pastaNome)
      }
      throw pastaError
    }

    const pastaRow = pasta as FinanceiroComissoesIcatuPasta
    const payloads = input.rows.map((r) => rowToInsert(r, pastaRow.id, input.userId))
    const totalRows = payloads.length

    input.onProgress?.({ pastaNome, savedRows: 0, totalRows })

    for (let i = 0; i < payloads.length; i += BATCH_SIZE) {
      const chunk = payloads.slice(i, i + BATCH_SIZE)
      const { error: rowsError } = await supabase.from(ROWS_TABLE).insert(chunk)
      if (rowsError) throw rowsError
      input.onProgress?.({
        pastaNome,
        savedRows: Math.min(i + chunk.length, totalRows),
        totalRows,
      })
    }

    return pastaRow
  },
}
